/* ============================================================================
 * Brand feedback rating — shared color system
 * ============================================================================
 * One rating scale (1-5, cr_hire_feedback.rating), rendered in two places:
 * HireFeedbackCard (full detail on the hire page) and HireRatingStars (compact
 * inline + tooltip in the Creator Payments table). Both read this single
 * RATING_META so the "premium" graded look — same tone per score — can't
 * drift between the two the way two copy-pasted color tables eventually would.
 * ========================================================================== */

export type HireFeedback = {
  hire_id: string;
  rating: number;
  notes: string | null;
  submitted_by_email?: string | null;
  created_at?: string;
};

export const RATING_META: Record<number, {
  label: string;
  /** Chip classes for the "4/5 · Great" badge. */
  badge: string;
  /** Filled-star color. */
  star: string;
  /** Ring tint used on the score medallion + card accent (includes opacity). */
  ring: string;
  /** Plain border color, for surfaces (tooltip/quote) that need a solid edge. */
  border: string;
  /** Gradient background for the score medallion. */
  medallion: string;
  /** Text color for the big numeral. */
  score: string;
  /** Background tint for the notes quote block. */
  quoteBg: string;
}> = {
  1: { label: 'Poor',        badge: 'bg-red-100 text-red-800 border-red-200',           star: 'text-red-500',     ring: 'ring-red-200/80',     border: 'border-red-300',     medallion: 'bg-gradient-to-br from-red-50 to-red-100',         score: 'text-red-600',     quoteBg: 'bg-red-50/50' },
  2: { label: 'Below par',   badge: 'bg-orange-100 text-orange-800 border-orange-200',  star: 'text-orange-500',  ring: 'ring-orange-200/80',  border: 'border-orange-300',  medallion: 'bg-gradient-to-br from-orange-50 to-orange-100',   score: 'text-orange-600',  quoteBg: 'bg-orange-50/50' },
  3: { label: 'Solid',       badge: 'bg-amber-100 text-amber-800 border-amber-200',     star: 'text-amber-500',   ring: 'ring-amber-200/80',   border: 'border-amber-300',   medallion: 'bg-gradient-to-br from-amber-50 to-amber-100',     score: 'text-amber-600',   quoteBg: 'bg-amber-50/50' },
  4: { label: 'Great',       badge: 'bg-lime-100 text-lime-800 border-lime-200',        star: 'text-lime-600',    ring: 'ring-lime-200/80',    border: 'border-lime-300',    medallion: 'bg-gradient-to-br from-lime-50 to-lime-100',       score: 'text-lime-700',    quoteBg: 'bg-lime-50/50' },
  5: { label: 'Outstanding', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', star: 'text-emerald-600', ring: 'ring-emerald-200/80', border: 'border-emerald-300', medallion: 'bg-gradient-to-br from-emerald-50 to-emerald-100', score: 'text-emerald-700', quoteBg: 'bg-emerald-50/50' },
};
